export interface ApiErrorBody {
    message?: string
    error?: string
    errors?: Record<string, string[]>
    /** On a uniqueness failure: attribute name => public ID of the record already using that value. */
    conflicts?: Record<string, string>
    [key: string]: unknown
}

/** What we know about the request and response besides the parsed JSON body. */
export interface ApiErrorContext {
    method?: string
    url?: string
    /** The raw response body, when it wasn't JSON (e.g. a proxy's error page). */
    responseText?: string
}

const GATEWAY_STATUSES = [502, 503, 504]
const RESPONSE_TEXT_DETAIL_LIMIT = 300

/**
 * Custom error class for API responses.
 * Preserves HTTP status, error code, message, and validation errors from the response body.
 *
 * A response without a JSON message (Heroku's router answers a request that ran past its 30s limit
 * with a 503 HTML page) gets a readable `message` for everyone, and keeps the technical `detail`
 * for internal admins ({@link messageFor}).
 */
export class ApiError extends Error {
    status: number
    code: string | undefined
    errors: Record<string, string[]>
    body: ApiErrorBody
    /** Status, request and any non-HTML response text: what an internal admin needs to debug it. */
    detail: string

    constructor(status: number, body: ApiErrorBody = {}, context: ApiErrorContext = {}) {
        super(body.message || body.error || fallbackMessage(status, context.method))
        this.name = 'ApiError'
        this.status = status
        this.code = body.error
        this.errors = body.errors || {}
        this.body = body
        this.detail = buildDetail(status, context)
    }

    get isNotFound(): boolean {
        return this.status === 404
    }

    get isServerError(): boolean {
        return this.status >= 500
    }

    get isValidationError(): boolean {
        return this.status === 422
    }

    get isNetworkError(): boolean {
        return this.status === 0
    }

    /** True when the server sent no message of its own, so `message` is our generic fallback. */
    get hasFallbackMessage(): boolean {
        return !this.body.message && !this.body.error
    }

    /**
     * The message to show a user. Internal admins also get {@link detail} when the server sent no
     * message of its own; a server-written message is already the full story.
     */
    messageFor({isAdmin}: {isAdmin: boolean}): string {
        if (!isAdmin || !this.hasFallbackMessage) return this.message

        return `${this.message} Admin details: ${this.detail}`
    }
}

function fallbackMessage(status: number, method = 'GET'): string {
    if (GATEWAY_STATUSES.includes(status)) {
        // The router gave up waiting, but the app may still be working on the request.
        return method.toUpperCase() === 'GET'
            ? 'The server took too long to respond. Please try again in a moment.'
            : 'The server took too long to respond. Your request may still finish, so refresh the page before trying again.'
    }
    if (status >= 500) return 'Something went wrong on our end. Please try again in a moment.'

    return 'The request failed. Please try again.'
}

function buildDetail(status: number, {method, url, responseText}: ApiErrorContext): string {
    const request = [method?.toUpperCase(), url && pathOf(url)].filter(Boolean).join(' ')
    let detail = request ? `HTTP ${status} from ${request}` : `HTTP ${status}`

    // An HTML error page is markup, not a message.
    const text = responseText?.trim()
    if (text && !text.startsWith('<')) detail += `: ${text.slice(0, RESPONSE_TEXT_DETAIL_LIMIT)}`

    return detail
}

function pathOf(url: string): string {
    try {
        const parsed = new URL(url, 'http://localhost')
        return parsed.pathname + parsed.search
    } catch {
        return url
    }
}

/**
 * Creates an ApiError for network failures (no HTTP response).
 */
export function networkError(cause: unknown): ApiError {
    const err = new ApiError(0, { message: 'Network error. Please check your connection and try again.' })
    err.cause = cause
    return err
}
